import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, CircularProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip,
} from '@material-ui/core';
import { KeyboardArrowRight } from '@material-ui/icons';
import clsx from 'clsx';
import { useApolloClient } from '@apollo/client';
import { IPve } from '@pvplevelup/core/dist/interfaces/Pve/IPve';
import { IPveBreakdownRow, IPvePullBreakdown, PveBreakdownKind, mergePveBreakdowns } from '@pvplevelup/core/dist/interfaces/Pve/IPvePull';
import { PveBreakdownQuery, PveBreakdownData, PveBreakdownVars } from '@pvplevelup/core/dist/queries/pve/pve';
import { formatPveNumber, getPveDamageColor, PVE_DAMAGE_COLOR } from './pveFormat';
import { getAbility } from 'utilities/ability_icons';
import { classImages } from 'utilities/ClassImages';

const MUTED = 'rgba(255, 255, 255, 0.6)';
const ACCENT = '#42a5f5';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  controls: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(1.75),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      justifyItems: 'start',
    },
  },
  filters: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
    // room for the outlined fields' floating labels
    paddingTop: 6,
  },
  segmented: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 4,
    padding: 3,
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  segment: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 7,
    alignItems: 'center',
    height: 32,
    padding: '0 13px',
    borderRadius: 7,
    fontSize: '0.84rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: MUTED,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': {
      color: '#fff',
    },
  },
  segmentActive: {
    color: '#fff',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  groupLabel: {
    marginRight: 8,
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  field: {
    '& .MuiOutlinedInput-root': {
      height: 36,
      borderRadius: 9,
      fontSize: '0.86rem',
      background: 'rgba(0, 0, 0, 0.25)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.25)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: ACCENT,
      borderWidth: 1,
    },
    '& .MuiInputLabel-outlined': {
      color: MUTED,
    },
    '& .MuiSelect-icon': {
      color: MUTED,
    },
  },
  tableWrap: {
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.07)',
    background: 'rgba(0, 0, 0, 0.15)',
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      background: 'rgba(255, 255, 255, 0.15)',
    },
  },
  table: {
    // sortable headers
    '& .MuiTableSortLabel-root': {
      color: 'inherit',
      '&:hover': {
        color: '#fff',
      },
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: '#fff',
    },
    '& .MuiTableSortLabel-icon': {
      color: `${ACCENT} !important`,
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      fontVariantNumeric: 'tabular-nums',
    },
    '& .MuiTableCell-head': {
      fontSize: '0.68rem',
      fontWeight: 800,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      color: MUTED,
      background: '#1b1c21',
    },
  },
  groupRow: {
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': {
      backgroundColor: 'rgba(66, 165, 245, 0.05)',
    },
  },
  groupRowOpen: {
    backgroundColor: 'rgba(66, 165, 245, 0.06)',
    '& .MuiTableCell-root': {
      borderBottomColor: 'transparent',
    },
  },
  chevron: {
    display: 'block',
    fontSize: 20,
    color: MUTED,
    transition: 'transform 0.15s',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
    color: ACCENT,
  },
  childRow: {
    background: 'rgba(255, 255, 255, 0.02)',
    // the ability icon makes these taller than their parent row unless the cells give up some padding
    '& .MuiTableCell-root': {
      paddingTop: 3,
      paddingBottom: 3,
      fontSize: '0.82rem',
      color: 'rgba(255, 255, 255, 0.85)',
    },
  },
  nested: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridColumnGap: 4,
    alignItems: 'center',
    // the chevron is a click target of its own size; the row is clickable anyway
    '& svg': { fontSize: 18 },
  },
  chevronSpace: {
    display: 'inline-block',
    width: 18,
  },
  childName: {
    paddingLeft: '28px !important',
    boxShadow: `inset 18px 0 0 -16px rgba(66, 165, 245, 0.35)`,
  },
  bar: {
    position: 'relative',
    minWidth: 140,
    height: 22,
    display: 'grid',
    alignItems: 'center',
  },
  barTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 3,
    bottom: 3,
    borderRadius: 5,
    background: 'rgba(255, 255, 255, 0.04)',
  },
  barFill: {
    position: 'absolute',
    left: 0,
    top: 3,
    bottom: 3,
    borderRadius: 5,
    opacity: 0.45,
    transition: 'width 0.3s ease',
  },
  barText: {
    position: 'relative',
    paddingLeft: 8,
    fontWeight: 700,
  },
  name: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
  },
  abilityIcon: {
    display: 'block',
    width: 22,
    height: 22,
    borderRadius: 4,
  },
  strong: {
    fontWeight: 700,
    color: '#fff',
  },
  muted: {
    color: MUTED,
  },
  hint: {
    marginTop: theme.spacing(1),
    fontSize: '0.78rem',
    color: 'rgba(255, 255, 255, 0.45)',
  },
  empty: {
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    color: MUTED,
  },
}));

const KIND_COLORS: Record<string, string> = {
  [PveBreakdownKind.Damage]: PVE_DAMAGE_COLOR,
  [PveBreakdownKind.Taken]: '#1e88e5',
  [PveBreakdownKind.Heal]: '#43a047',
};

const KINDS = [
  {value: PveBreakdownKind.Damage, label: 'Damage Done', color: '#ef5350'},
  {value: PveBreakdownKind.Taken, label: 'Damage Taken', color: '#42a5f5'},
  {value: PveBreakdownKind.Heal, label: 'Healing', color: '#66bb6a'},
];

enum GroupBy {
  Target = "target",
  Ability = "ability",
  Player = "player",
}

/**
 * The breakdowns of the given pulls. Pulls already loaded aren't fetched again, except that a pull that's
 * still changing (live run) is refetched whenever its times change.
 */
export function usePveBreakdowns(pve: IPve, indexes: number[]) {
  const client = useApolloClient();
  const [cache, setCache] = useState<Record<number, IPvePullBreakdown>>({});
  const [loading, setLoading] = useState(false);
  // what each pull looked like when its breakdown was fetched
  const fetchedAs = useRef<Record<number, string>>({});
  const id = pve._id?.toString() || '';
  const pullsById: Record<number, string> = {};
  (pve.pulls || []).forEach((p) => pullsById[p.index] = `${p.start_time}-${p.end_time}`);

  useEffect(() => {
    setCache({});
    fetchedAs.current = {};
  }, [id]);

  const wanted = indexes.filter((index) => pullsById[index] != null && fetchedAs.current[index] != pullsById[index]);
  const wantedKey = wanted.join(',');

  useEffect(() => {
    if(!id || wanted.length == 0)
      return;
    let cancelled = false;
    const versions: Record<number, string> = {};
    wanted.forEach((index) => versions[index] = pullsById[index]);
    setLoading(true);
    client.query<PveBreakdownData, PveBreakdownVars>({query: PveBreakdownQuery, variables: {id, pulls: wanted}, fetchPolicy: 'network-only'})
      .then(({data}) => {
        if(cancelled)
          return;
        Object.assign(fetchedAs.current, versions);
        setCache((current) => {
          const next = {...current};
          wanted.forEach((index) => next[index] = {index, rows: []});
          (data?.pveBreakdown || []).forEach((b) => next[b.index] = b);
          return next;
        });
      })
      .catch((err) => console.log('Failed to load the breakdown', err))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, wantedKey]);

  const breakdowns = indexes.map((index) => cache[index]).filter((b) => b != null);
  return {breakdowns, loading: loading && breakdowns.length < indexes.length};
}

type BreakdownSortKey = 'name' | 'total' | 'hits' | 'critTotal' | 'crit' | 'average' | 'max' | 'maxCrit';

/** a group's value for a column (% and per second follow the total) */
const sortValue = (group: Group, key: BreakdownSortKey) =>
  key == 'total' ? group.total
  : key == 'hits' ? group.hits
  : key == 'critTotal' ? (group.critTotal ?? -1)
  : key == 'crit' ? (group.hits > 0 ? group.crits / group.hits : 0)
  : key == 'average' ? (group.hits > 0 ? group.total / group.hits : 0)
  : key == 'maxCrit' ? (group.maxCrit ?? -1)
  : group.max;

interface Group {
  key: string;
  /** who used it, to look up the right class's icon */
  players: Record<string, boolean>;
  total: number;
  hits: number;
  crits: number;
  /** null when any of its rows is from before crit amounts were counted */
  critTotal: number | null;
  max: number;
  maxCrit: number | null;
  children: Group[];
}

/** Rows grouped by the first key, each group's rows by the next one, and so on. */
const groupRows = (rows: IPveBreakdownRow[], keyOfs: Array<(row: IPveBreakdownRow) => string>): Group[] => {
  const [keyOf, ...childKeyOfs] = keyOfs;
  const groups: Record<string, {group: Group, rows: IPveBreakdownRow[]}> = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    const entry = groups[key] || (groups[key] = {group: {key, players: {}, total: 0, hits: 0, crits: 0, critTotal: 0, max: 0, maxCrit: 0, children: []}, rows: []});
    entry.group.players[row.player] = true;
    entry.group.total += row.total;
    entry.group.hits += row.hits;
    entry.group.crits += row.crits;
    entry.group.max = Math.max(entry.group.max, row.max);
    if(entry.group.critTotal == null || row.critTotal == null){
      entry.group.critTotal = null;
      entry.group.maxCrit = null;
    }else{
      entry.group.critTotal += row.critTotal;
      entry.group.maxCrit = Math.max(entry.group.maxCrit || 0, row.maxCrit || 0);
    }
    entry.rows.push(row);
  });
  return Object.keys(groups).map((key) => {
    const {group, rows: groupRowsList} = groups[key];
    if(childKeyOfs.length > 0)
      group.children = groupRows(groupRowsList, childKeyOfs);
    return group;
  }).sort((a, b) => b.total - a.total);
};

const KEY_OF: Record<GroupBy, (row: IPveBreakdownRow) => string> = {
  [GroupBy.Target]: (row) => row.other,
  [GroupBy.Ability]: (row) => row.ability,
  [GroupBy.Player]: (row) => row.player,
};

/** what a row expands into, level by level, for each Group by choice */
const LEVELS: Record<GroupBy, GroupBy[]> = {
  [GroupBy.Target]: [GroupBy.Target, GroupBy.Player, GroupBy.Ability],
  [GroupBy.Ability]: [GroupBy.Ability, GroupBy.Player, GroupBy.Target],
  [GroupBy.Player]: [GroupBy.Player, GroupBy.Target, GroupBy.Ability],
};

interface Props {
  pve: IPve;
  /** the selected pulls */
  indexes: number[];
  /** combined length of the selected pulls in seconds, for per-second numbers */
  duration: number;
  /** already in memory (e.g. the Boss Explorer); skips fetching */
  breakdowns?: IPvePullBreakdown[];
}

/** What each player did to (or took from, or healed on) whom, with which abilities, over the selected pulls. */
export const PveBreakdown: React.FC<Props> = ({pve, indexes, duration, breakdowns: local}) => {
  const classes = useStyles();
  const fetched = usePveBreakdowns(pve, local ? [] : indexes);
  const breakdowns = local ? local.filter((b) => indexes.indexOf(b.index) != -1) : fetched.breakdowns;
  const [kind, setKind] = useState<PveBreakdownKind>(PveBreakdownKind.Damage);
  const [player, setPlayer] = useState<string>('');
  const [other, setOther] = useState<string>('');
  const [groupBy, setGroupBy] = useState<GroupBy>(GroupBy.Target);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const rows = useMemo(() => mergePveBreakdowns(breakdowns), [breakdowns]);
  const ofKind = rows.filter((r) => r.kind == kind);
  const players = Array.from(new Set(ofKind.map((r) => r.player))).sort();
  const others = Array.from(new Set(ofKind.filter((r) => !player || r.player == player).map((r) => r.other))).sort();
  const filtered = ofKind.filter((r) => (!player || r.player == player) && (!other || r.other == other));

  // a filter that no longer exists in the selection is dropped
  useEffect(() => {
    if(player && players.indexOf(player) == -1) setPlayer('');
  }, [players.join('|')]);
  useEffect(() => {
    if(other && others.indexOf(other) == -1) setOther('');
  }, [others.join('|')]);

  const levels = LEVELS[groupBy];
  // click a header to sort by it, again to flip; numbers start highest first, names A-Z. Expanded rows follow the same order.
  const [sort, setSort] = useState<{key: BreakdownSortKey, desc: boolean}>({key: 'total', desc: true});
  const sortGroups = (list: Group[]): Group[] => [...list].sort((a, b) => {
    const diff = sort.key == 'name' ? a.key.localeCompare(b.key) : sortValue(a, sort.key) - sortValue(b, sort.key);
    return sort.desc ? -diff : diff;
  }).map((group) => ({...group, children: sortGroups(group.children || [])}));
  const groups = sortGroups(groupRows(filtered, levels.map((g) => KEY_OF[g])));
  const sortCell = (key: BreakdownSortKey, text: React.ReactNode, align?: 'right') => (
    <TableCell align={align} sortDirection={sort.key == key ? (sort.desc ? 'desc' : 'asc') : false}>
      <TableSortLabel active={sort.key == key} direction={sort.key == key && !sort.desc ? 'asc' : 'desc'}
        onClick={() => setSort((current) => current.key == key ? {key, desc: !current.desc} : {key, desc: key != 'name'})}>
        {text}
      </TableSortLabel>
    </TableCell>
  );
  const total = groups.reduce((sum, g) => sum + g.total, 0);
  const otherLabel = kind == PveBreakdownKind.Damage ? 'Target' : kind == PveBreakdownKind.Taken ? 'Source' : 'Healed';
  const label = (g: GroupBy) => g == GroupBy.Target ? otherLabel : g == GroupBy.Ability ? 'Ability' : 'Player';
  const color = KIND_COLORS[kind];
  // monsters' abilities have no icons, so damage taken goes without
  const showIcons = kind != PveBreakdownKind.Taken;
  const classOf = (players: Record<string, boolean>) => {
    const names = Object.keys(players);
    const classes = Array.from(new Set(names.map((name) => (pve.players || []).find((p) => p.name == name)?.class || '')));
    // used by players of one class: that class's icon; otherwise search every class
    return classes.length == 1 ? classes[0] : '';
  };
  const classOfPlayer = (name: string) => (pve.players || []).find((p) => p.name == name)?.class || '';
  const nameCell = (g: GroupBy, group: Group) => showIcons && g == GroupBy.Ability
    ? <span className={classes.name}><img className={classes.abilityIcon} src={getAbility(group.key, classOf(group.players))} />{group.key}</span>
    : g == GroupBy.Player && classOfPlayer(group.key)
      ? <span className={classes.name}><img className={classes.abilityIcon} src={classImages.getColorIcon(classOfPlayer(group.key))} />{group.key}</span>
      : group.key;
  // what each level is called in the hint
  const levelNoun = (g: GroupBy) => g == GroupBy.Ability ? 'ability' : g == GroupBy.Player ? 'player'
    : kind == PveBreakdownKind.Damage ? 'target' : kind == PveBreakdownKind.Taken ? 'source' : 'who was healed';

  /**
   * A row and, while it's open, the rows it expands into. Open rows are remembered by their path (target/player), so the
   * same player under two targets opens on its own. Bars and % are relative to the row above.
   */
  const renderGroup = (group: Group, depth: number, path: string, scale: number): React.ReactNode => {
    const key = `${path}/${group.key}`;
    const hasChildren = (group.children || []).length > 0;
    const open = hasChildren && expanded[key] == true;
    const toggle = hasChildren ? () => setExpanded({...expanded, [key]: !open}) : undefined;
    const chevron = <KeyboardArrowRight className={clsx(classes.chevron, {[classes.chevronOpen]: open})} />;
    return (
      <React.Fragment key={key}>
        <TableRow className={clsx(depth == 0 ? classes.groupRow : classes.childRow, {[classes.groupRow]: hasChildren, [classes.groupRowOpen]: open})} onClick={toggle}>
          <TableCell padding="checkbox" style={{paddingLeft: 12}}>
            {depth == 0 && hasChildren && chevron}
          </TableCell>
          <TableCell className={depth == 0 ? classes.strong : classes.childName}>
            {depth == 0 ? nameCell(levels[depth], group) : (
              <span className={classes.nested} style={{marginLeft: (depth - 1) * 22}}>
                {hasChildren ? chevron : <span className={classes.chevronSpace} />}
                {nameCell(levels[depth], group)}
              </span>
            )}
          </TableCell>
          {cells(group, scale, depth == 0 ? total : scale)}
        </TableRow>
        {open && group.children.map((child) => renderGroup(child, depth + 1, key, group.total))}
      </React.Fragment>
    );
  };

  // damage done by players of one class takes that class's color
  const barColor = (g: Group) => kind == PveBreakdownKind.Damage ? getPveDamageColor(classOf(g.players)) : color;

  // max sizes the bar, of the share in %: the row above for nested rows, the whole tab at the top
  const cells = (g: Group, max: number, of: number = total) => (
    <React.Fragment>
      <TableCell>
        <div className={classes.bar}>
          <div className={classes.barTrack} />
          <div className={classes.barFill} style={{width: `${max > 0 ? Math.max(2, Math.round((g.total / max) * 100)) : 0}%`, background: `linear-gradient(90deg, ${barColor(g)}, ${barColor(g)}aa)`}} />
          <span className={classes.barText}>{formatPveNumber(g.total)}</span>
        </div>
      </TableCell>
      <TableCell align="right">{of > 0 ? `${((g.total / of) * 100).toFixed(1)}%` : '-'}</TableCell>
      <TableCell align="right" className={classes.strong}>{duration > 0 ? formatPveNumber(g.total / duration) : '-'}</TableCell>
      <TableCell align="right" className={classes.muted}>{g.hits.toLocaleString()}</TableCell>
      <TableCell align="right">
        {g.critTotal == null ? '-' : (
          <Tooltip title={g.total > 0 ? `${Math.round((g.critTotal / g.total) * 100)}% of the total came from crits` : ''}>
            <span>{formatPveNumber(g.critTotal)}</span>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right">{g.hits > 0 ? `${Math.round((g.crits / g.hits) * 100)}%` : '-'}</TableCell>
      <TableCell align="right" className={classes.muted}>{g.hits > 0 ? formatPveNumber(g.total / g.hits) : '-'}</TableCell>
      <TableCell align="right">{formatPveNumber(g.max)}</TableCell>
      <TableCell align="right">{g.maxCrit == null ? '-' : g.maxCrit > 0 ? formatPveNumber(g.maxCrit) : '-'}</TableCell>
    </React.Fragment>
  );

  // the bars stay scaled to the biggest total whatever the order
  const maxGroup = Math.max(0, ...groups.map((g) => g.total));

  return (
    <Box>
      <div className={classes.controls}>
        <div className={classes.segmented}>
          {KINDS.map((k) => (
            <div key={k.value} role="button" className={clsx(classes.segment, {[classes.segmentActive]: kind == k.value})} onClick={() => setKind(k.value)}
              style={kind == k.value ? {boxShadow: `inset 0 -2px 0 ${k.color}`} : undefined}>
              <span className={classes.dot} style={{background: k.color, boxShadow: kind == k.value ? `0 0 6px ${k.color}` : 'none'}} />
              <span>{k.label}</span>
            </div>
          ))}
        </div>
        <div className={classes.filters}>
          <TextField select size="small" variant="outlined" label="Player" value={player} className={classes.field} style={{minWidth: 170}}
            onChange={(e) => setPlayer(e.target.value as string)} SelectProps={{displayEmpty: true}} InputLabelProps={{shrink: true}}>
            <MenuItem value="">Everyone</MenuItem>
            {players.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField select size="small" variant="outlined" label={otherLabel} value={other} className={classes.field} style={{minWidth: 200}}
            onChange={(e) => setOther(e.target.value as string)} SelectProps={{displayEmpty: true}} InputLabelProps={{shrink: true}}>
            <MenuItem value="">All</MenuItem>
            {others.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </div>
        <div style={{display: 'grid', gridAutoFlow: 'column', alignItems: 'center'}}>
          <span className={classes.groupLabel}>Group by</span>
          <div className={classes.segmented}>
            {[GroupBy.Target, GroupBy.Ability, GroupBy.Player].map((g) => (
              <div key={g} role="button" className={clsx(classes.segment, {[classes.segmentActive]: groupBy == g})}
                style={groupBy == g ? {boxShadow: `inset 0 -2px 0 ${ACCENT}`} : undefined}
                onClick={() => { setGroupBy(g); setExpanded({}); }}>{label(g)}</div>
            ))}
          </div>
        </div>
      </div>
      {fetched.loading && !local && <Box p={3} textAlign="center"><CircularProgress size={28} /></Box>}
      {!(fetched.loading && !local) &&
        <TableContainer className={classes.tableWrap} style={{maxHeight: 600}}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {sortCell('name', label(groupBy))}
                {sortCell('total', 'Total')}
                {sortCell('total', '%', 'right')}
                {sortCell('total', 'Per sec', 'right')}
                {sortCell('hits', kind == PveBreakdownKind.Heal ? 'Heals' : 'Hits', 'right')}
                {sortCell('critTotal', kind == PveBreakdownKind.Heal ? 'Crit heal' : 'Crit dmg', 'right')}
                {sortCell('crit', 'Crit', 'right')}
                {sortCell('average', 'Average', 'right')}
                {sortCell('max', 'Biggest', 'right')}
                {sortCell('maxCrit', 'Biggest crit', 'right')}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => renderGroup(group, 0, '', maxGroup))}
              {groups.length == 0 &&
                <TableRow><TableCell colSpan={11} className={classes.empty}>Nothing recorded for this selection.</TableCell></TableRow>
              }
            </TableBody>
          </Table>
        </TableContainer>
      }
      <div className={classes.hint}>
        Click a row to break it down by {levelNoun(levels[1])}, then by {levelNoun(levels[2])}. Healing only counts heals on people in the run.
      </div>
    </Box>
  );
}
